import { useMemo, useState } from "react";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ExpandableRow } from "@/components/ui/responsive-table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { AdminCountryPills } from "@/components/admin/country-pills";
import { useAdminCountries } from "@/hooks/use-admin-countries";
import { showToast } from "@/lib/ui/toast";

/**
 * Admin → Subscriptions.
 *
 * Port of src/app/admin/subscriptions/{page,subscriptions-client}.tsx
 * (legacy ~550 LOC client) per D21 NO REINVENTION.
 *
 * Optimizations declared:
 *   - raw fetch + useAdminFetch hook → apiFetch (Architecture Discipline Rule 1)
 *   - useTranslations next-intl → Lingui Trans/t macros (D4)
 *   - useState/useEffect for fetch → useQuery + useMutation (cache + auto-refetch)
 *   - window.location.reload() → React Query cache invalidation
 *
 * Endpoints consumed (apps/api admin-subscriptions — all live):
 *   GET /api/admin/subscriptions       — list with limit
 *   GET /api/admin/subscriptions/kpis  — active/MRR/founding/churn/trial counts
 *   PUT /api/admin/subscriptions       — body { id, action } where action ∈
 *     "cancel" | "pause" | "resume" | "grantFounding"
 */

interface PaymentInfo {
  id: string;
  amount: number;
  currency: string;
  status: string;
  paidAt: string | null;
  invoiceNumber: string | null;
}

interface SubscriptionRaw {
  id: string;
  userId: string;
  user?: { email?: string; userType?: string };
  entityType: string;
  entityId: string;
  planTier: string;
  planInterval: string;
  price: number | string;
  currency: string;
  status: string;
  isFounding: boolean;
  isGrandfathered: boolean;
  trialEndsAt: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  scheduledPlanTier: string | null;
  scheduledChangeAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  stripeSubscriptionId: string | null;
  country: string | null;
  createdAt: string;
  payments?: {
    id: string;
    amount: number | string;
    currency: string;
    status: string;
    paidAt: string | null;
    invoiceNumber: string | null;
  }[];
}

interface SubscriptionRow {
  id: string;
  userId: string;
  userEmail: string;
  userType: string;
  entityType: string;
  entityId: string;
  planTier: string;
  planInterval: string;
  price: number;
  currency: string;
  status: string;
  isFounding: boolean;
  isGrandfathered: boolean;
  trialEndsAt: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  scheduledPlanTier: string | null;
  scheduledChangeAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  stripeSubscriptionId: string | null;
  country: string | null;
  createdAt: string;
  payments: PaymentInfo[];
}

interface KPIs {
  activeCount: number;
  mrr: number;
  foundingCount: number;
  churnRate: number;
  trialingCount: number;
  totalCount: number;
}

const planColors: Record<string, string> = {
  free: "bg-zinc-100 text-zinc-600",
  basic: "bg-blue-100 text-blue-700",
  pro: "bg-indigo-100 text-indigo-700",
  expert: "bg-purple-100 text-purple-700",
  premium: "bg-amber-100 text-amber-700",
  priority: "bg-teal-100 text-teal-700",
};

const statusColors: Record<string, string> = {
  active: "border-emerald-300 bg-emerald-50 text-emerald-700",
  trialing: "border-blue-300 bg-blue-50 text-blue-700",
  past_due: "border-amber-300 bg-amber-50 text-amber-700",
  paused: "border-zinc-300 bg-zinc-50 text-zinc-600",
  cancelled: "border-red-300 bg-red-50 text-red-700",
};

function entityLabel(t: ReturnType<typeof useLingui>["t"], entityType: string): string {
  switch (entityType) {
    case "provider":
      return `👨‍⚕️ ${t(msg`Provider`)}`;
    case "facility":
      return `🏥 ${t(msg`Facility`)}`;
    case "patient":
    case "user":
      return `🧑 ${t(msg`User`)}`;
    default:
      return entityType;
  }
}

function transformRow(s: SubscriptionRaw): SubscriptionRow {
  return {
    id: s.id,
    userId: s.userId,
    userEmail: s.user?.email ?? "",
    userType: s.user?.userType ?? "",
    entityType: s.entityType,
    entityId: s.entityId,
    planTier: s.planTier,
    planInterval: s.planInterval,
    price: Number(s.price),
    currency: s.currency,
    status: s.status,
    isFounding: s.isFounding,
    isGrandfathered: s.isGrandfathered,
    trialEndsAt: s.trialEndsAt ?? null,
    currentPeriodStart: s.currentPeriodStart ?? null,
    currentPeriodEnd: s.currentPeriodEnd ?? null,
    scheduledPlanTier: s.scheduledPlanTier ?? null,
    scheduledChangeAt: s.scheduledChangeAt ?? null,
    cancelledAt: s.cancelledAt ?? null,
    cancelReason: s.cancelReason ?? null,
    stripeSubscriptionId: s.stripeSubscriptionId ?? null,
    country: s.country ?? null,
    createdAt: s.createdAt,
    payments: (s.payments ?? []).map((p) => ({
      id: p.id,
      amount: Number(p.amount),
      currency: p.currency,
      status: p.status,
      paidAt: p.paidAt ?? null,
      invoiceNumber: p.invoiceNumber ?? null,
    })),
  };
}

export const Route = createFileRoute("/_authenticated/subscriptions")({
  component: SubscriptionsPage,
});

function SubscriptionsPage() {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const { visibleCountries: countries } = useAdminCountries();

  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [filterCountry, setFilterCountry] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const subscriptionsQuery = useQuery({
    queryKey: ["admin", "subscriptions", "list"],
    queryFn: async (): Promise<{ rows: SubscriptionRow[]; total: number }> => {
      try {
        const data = await apiFetch.get<{ subscriptions: SubscriptionRaw[]; total: number }>(
          "/api/admin/subscriptions?limit=100",
        );
        return {
          rows: (data.subscriptions ?? []).map(transformRow),
          total: data.total ?? 0,
        };
      } catch (error) {
        if (error instanceof ApiError) return { rows: [], total: 0 };
        throw error;
      }
    },
    staleTime: 30_000,
  });

  const kpisQuery = useQuery({
    queryKey: ["admin", "subscriptions", "kpis"],
    queryFn: async (): Promise<KPIs> => {
      try {
        const data = await apiFetch.get<Partial<KPIs>>("/api/admin/subscriptions/kpis");
        return {
          activeCount: data.activeCount ?? 0,
          mrr: data.mrr ?? 0,
          foundingCount: data.foundingCount ?? 0,
          churnRate: data.churnRate ?? 0,
          trialingCount: data.trialingCount ?? 0,
          totalCount: data.totalCount ?? 0,
        };
      } catch (error) {
        if (error instanceof ApiError)
          return {
            activeCount: 0,
            mrr: 0,
            foundingCount: 0,
            churnRate: 0,
            trialingCount: 0,
            totalCount: 0,
          };
        throw error;
      }
    },
    staleTime: 30_000,
  });

  const subscriptions = subscriptionsQuery.data?.rows ?? [];
  const total = subscriptionsQuery.data?.total ?? 0;
  // The open row as the list has it now: an action's refetch shows in the
  // sheet at once (a pending cancel hides Cancel).
  const selected = selectedId ? (subscriptions.find((s) => s.id === selectedId) ?? null) : null;
  const kpis = kpisQuery.data ?? {
    activeCount: 0,
    mrr: 0,
    foundingCount: 0,
    churnRate: 0,
    trialingCount: 0,
    totalCount: total,
  };

  const filtered = useMemo(
    () =>
      subscriptions.filter((s) => {
        if (filterCountry && s.country !== filterCountry) return false;
        if (filterType && s.entityType !== filterType) return false;
        if (filterStatus && s.status !== filterStatus) return false;
        if (search && !s.userEmail.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
      }),
    [subscriptions, filterCountry, filterType, filterStatus, search],
  );

  const countryCounts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const s of subscriptions) {
      if (s.country) map[s.country] = (map[s.country] || 0) + 1;
    }
    return map;
  }, [subscriptions]);

  const actionMutation = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: string }) => {
      await apiFetch.put("/api/admin/subscriptions", { id, action });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "subscriptions"] });
    },
    onError: () => {
      showToast(t`Action failed. Try again.`, "error");
    },
  });

  const isLoading = subscriptionsQuery.isLoading || kpisQuery.isLoading;

  if (isLoading && subscriptions.length === 0) {
    return (
      <div className="text-muted-foreground p-8 text-sm">
        <Trans>Loading subscriptions…</Trans>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-foreground text-2xl font-bold tracking-tight">
            <Trans>Subscriptions</Trans>
          </h1>
          <p className="text-muted-foreground text-sm">
            <Trans>
              {kpis.totalCount} subscriptions total, {kpis.activeCount} active
            </Trans>
          </p>
        </div>
      </div>

      <AdminCountryPills
        countries={countries.map((c) => ({ code: c.code, name: c.name, flag: c.flag }))}
        counts={countryCounts}
        selected={filterCountry}
        onSelect={setFilterCountry}
        totalCount={subscriptions.length}
      />

      <div className="grid gap-4 sm:grid-cols-5">
        {[
          { label: <Trans>Active</Trans>, value: kpis.activeCount.toString(), icon: "💳" },
          { label: <Trans>MRR</Trans>, value: `€${kpis.mrr.toLocaleString()}`, icon: "📈" },
          { label: <Trans>Founding</Trans>, value: kpis.foundingCount.toString(), icon: "🏆" },
          { label: <Trans>Churn</Trans>, value: `${kpis.churnRate}%`, icon: "📉" },
          { label: <Trans>Trial</Trans>, value: kpis.trialingCount.toString(), icon: "🧪" },
        ].map((s, i) => (
          <Card key={i}>
            <CardContent className="flex items-center gap-3 p-4">
              <span className="text-2xl">{s.icon}</span>
              <div>
                <div className="text-xl font-bold">{s.value}</div>
                <div className="text-muted-foreground text-xs">{s.label}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder={t`Search by email…`}
          className="w-64"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="flex gap-1">
          {(
            [
              { key: null, label: t`All` },
              { key: "provider", label: `👨‍⚕️ ${t`Providers`}` },
              { key: "facility", label: `🏥 ${t`Facilities`}` },
              { key: "user", label: `🧑 ${t`Users`}` },
            ] as const
          ).map((f) => (
            <Button
              key={f.label}
              variant={filterType === f.key ? "default" : "outline"}
              size="sm"
              onClick={() => setFilterType(f.key)}
            >
              {f.label}
            </Button>
          ))}
        </div>
        <div className="flex gap-1">
          {(
            [
              { key: null, label: t`Any status` },
              { key: "active", label: t`Active` },
              { key: "trialing", label: t`Trial` },
              { key: "cancelled", label: t`Cancelled` },
              { key: "paused", label: t`Paused` },
            ] as const
          ).map((f) => (
            <Button
              key={f.label}
              variant={filterStatus === f.key ? "default" : "outline"}
              size="sm"
              onClick={() => setFilterStatus(f.key)}
            >
              {f.label}
            </Button>
          ))}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            <Trans>{filtered.length} subscriptions</Trans>
            {filterType ? ` (${filterType})` : ""}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="text-muted-foreground p-8 text-center text-sm">
              {subscriptions.length === 0 ? (
                <Trans>
                  No subscriptions yet. Will be created automatically when providers/facilities
                  subscribe.
                </Trans>
              ) : (
                <Trans>No results for the selected filters.</Trans>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <Trans>Email</Trans>
                  </TableHead>
                  <TableHead className="hidden md:table-cell">
                    <Trans>Type</Trans>
                  </TableHead>
                  <TableHead className="hidden sm:table-cell">
                    <Trans>Plan</Trans>
                  </TableHead>
                  <TableHead className="hidden sm:table-cell">
                    <Trans>Interval</Trans>
                  </TableHead>
                  <TableHead className="hidden md:table-cell">
                    <Trans>Price</Trans>
                  </TableHead>
                  <TableHead>
                    <Trans>Status</Trans>
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">
                    <Trans>Founding</Trans>
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">
                    <Trans>Start</Trans>
                  </TableHead>
                  <TableHead className="w-8 md:hidden" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((sub) => (
                  <ExpandableRow
                    key={sub.id}
                    className="hover:bg-muted/30 cursor-pointer"
                    mobileDetails={[
                      { label: <Trans>Type</Trans>, value: entityLabel(t, sub.entityType) },
                      {
                        label: <Trans>Price</Trans>,
                        value: sub.price > 0 ? `€${sub.price}` : t`Free`,
                      },
                      {
                        label: <Trans>Plan</Trans>,
                        value: (
                          <>
                            <Badge variant="secondary" className={planColors[sub.planTier] || ""}>
                              {sub.planTier}
                            </Badge>
                            <ScheduledChange sub={sub} />
                          </>
                        ),
                      },
                      { label: <Trans>Interval</Trans>, value: sub.planInterval },
                      { label: <Trans>Founding</Trans>, value: sub.isFounding ? "🏆" : "—" },
                      {
                        label: <Trans>Start</Trans>,
                        value: new Date(sub.createdAt).toLocaleDateString(),
                      },
                    ]}
                  >
                    <TableCell
                      className="text-xs font-medium"
                      onClick={() => setSelectedId(sub.id)}
                    >
                      {sub.userEmail}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <span className="text-xs">{entityLabel(t, sub.entityType)}</span>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <Badge variant="secondary" className={planColors[sub.planTier] || ""}>
                        {sub.planTier}
                      </Badge>
                      <ScheduledChange sub={sub} />
                    </TableCell>
                    <TableCell className="hidden text-xs sm:table-cell">
                      {sub.planInterval}
                    </TableCell>
                    <TableCell className="hidden text-xs font-medium md:table-cell">
                      {sub.price > 0 ? `€${sub.price}` : <Trans>Free</Trans>}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={statusColors[sub.status] || ""}>
                        {sub.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {sub.isFounding ? "🏆" : "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground hidden text-xs lg:table-cell">
                      {new Date(sub.createdAt).toLocaleDateString()}
                    </TableCell>
                  </ExpandableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelectedId(null)}>
        <SheetContent className="overflow-y-auto sm:max-w-[500px]">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="text-lg">
                  {entityLabel(t, selected.entityType)} — {selected.planTier.toUpperCase()}
                </SheetTitle>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                <div className="space-y-2 text-sm">
                  <Row
                    label={<Trans>Email</Trans>}
                    value={<span className="font-medium">{selected.userEmail}</span>}
                  />
                  <Row
                    label={<Trans>Plan</Trans>}
                    value={
                      <Badge className={planColors[selected.planTier] || ""}>
                        {selected.planTier}
                      </Badge>
                    }
                  />
                  <Row
                    label={<Trans>Price</Trans>}
                    value={
                      selected.price > 0
                        ? `€${selected.price}/${selected.planInterval === "annual" ? t`year` : t`month`}`
                        : t`Free`
                    }
                  />
                  <Row
                    label={<Trans>Status</Trans>}
                    value={
                      <Badge variant="outline" className={statusColors[selected.status] || ""}>
                        {selected.status}
                      </Badge>
                    }
                  />
                  <Row
                    label={<Trans>Founding</Trans>}
                    value={selected.isFounding ? `🏆 ${t`Yes`}` : t`No`}
                  />
                  {selected.currentPeriodStart && (
                    <Row
                      label={<Trans>Current period</Trans>}
                      value={
                        <span className="text-xs">
                          {new Date(selected.currentPeriodStart).toLocaleDateString()} —{" "}
                          {selected.currentPeriodEnd
                            ? new Date(selected.currentPeriodEnd).toLocaleDateString()
                            : "—"}
                        </span>
                      }
                    />
                  )}
                  {selected.scheduledPlanTier && selected.scheduledChangeAt && (
                    <Row
                      label={<Trans>Scheduled change</Trans>}
                      value={<ScheduledChange sub={selected} />}
                    />
                  )}
                  {selected.stripeSubscriptionId && (
                    <Row
                      label={<Trans>Stripe</Trans>}
                      value={
                        <span className="font-mono text-xs">{selected.stripeSubscriptionId}</span>
                      }
                    />
                  )}
                  <Row
                    label={<Trans>Created</Trans>}
                    value={
                      <span className="text-xs">
                        {new Date(selected.createdAt).toLocaleString()}
                      </span>
                    }
                  />
                  {selected.cancelledAt && (
                    <Row
                      label={<Trans>Cancelled</Trans>}
                      value={
                        <span className="text-xs text-red-600">
                          {new Date(selected.cancelledAt).toLocaleString()}
                          {selected.cancelReason ? ` (${selected.cancelReason})` : ""}
                        </span>
                      }
                    />
                  )}
                </div>

                {selected.payments.length > 0 && (
                  <div>
                    <h3 className="mb-2 text-sm font-semibold">
                      <Trans>Recent payments</Trans>
                    </h3>
                    <div className="space-y-1">
                      {selected.payments.map((p) => (
                        <div
                          key={p.id}
                          className="bg-muted/30 flex items-center justify-between rounded px-3 py-2 text-xs"
                        >
                          <span>
                            {p.paidAt ? new Date(p.paidAt).toLocaleDateString() : t`Pending`}
                          </span>
                          <span className="font-medium">€{p.amount}</span>
                          <Badge
                            variant="outline"
                            className={
                              p.status === "completed" ? "border-emerald-300 text-emerald-700" : ""
                            }
                          >
                            {p.status}
                          </Badge>
                          {p.invoiceNumber && <span className="font-mono">{p.invoiceNumber}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="mb-2 text-sm font-semibold">
                    <Trans>Admin actions</Trans>
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selected.status === "active" && (
                      <>
                        {/* A cancel already pending runs to the period end. */}
                        {!selected.cancelledAt && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              actionMutation.mutate({ id: selected.id, action: "cancel" })
                            }
                            disabled={actionMutation.isPending}
                          >
                            <Trans>Cancel</Trans>
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            actionMutation.mutate({ id: selected.id, action: "pause" })
                          }
                          disabled={actionMutation.isPending}
                        >
                          <Trans>Pause</Trans>
                        </Button>
                        {!selected.isFounding && selected.entityType === "provider" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              actionMutation.mutate({ id: selected.id, action: "grantFounding" })
                            }
                            disabled={actionMutation.isPending}
                          >
                            <Trans>🏆 Founding</Trans>
                          </Button>
                        )}
                      </>
                    )}
                    {selected.status === "paused" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => actionMutation.mutate({ id: selected.id, action: "resume" })}
                        disabled={actionMutation.isPending}
                      >
                        <Trans>Resume</Trans>
                      </Button>
                    )}
                    {selected.status === "cancelled" && (
                      <span className="text-muted-foreground text-xs">
                        <Trans>Subscription cancelled</Trans>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/**
 * A downgrade waiting for the period end: the row keeps its paid plan until
 * the renewal, when the webhook writes the scheduled one
 * (apps/api/src/lib/subscriptions/scheduled-change.ts).
 */
function ScheduledChange({ sub }: { sub: SubscriptionRow }) {
  if (!sub.scheduledPlanTier || !sub.scheduledChangeAt) return null;
  const tier = sub.scheduledPlanTier;
  const date = new Date(sub.scheduledChangeAt).toLocaleDateString();
  return (
    <span className="text-warning mt-0.5 block text-xs">
      <Trans>
        → {tier} on {date}
      </Trans>
    </span>
  );
}

function Row({ label, value }: { label: React.ReactNode; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span>{value}</span>
    </div>
  );
}
